import {
  BriefcaseBusiness,
  Database,
  LandPlot,
  ListChecks,
  type LucideIcon,
} from 'lucide-react'
import { Link, useRouterState } from '@tanstack/react-router'

import { cn } from '@/lib/utils'

/** Plotea's pages beside the analysis, at the foot of its one left rail. */
const PAGES: readonly { to: string; label: string; icon: LucideIcon }[] = [
  { to: '/site/portfolio', label: 'Portfolio', icon: BriefcaseBusiness },
  { to: '/site/screening', label: 'Przesiew', icon: ListChecks },
  { to: '/site/sources', label: 'Źródła danych', icon: Database },
]

/** One rail entry in the module rail's look: icon over a short label. */
export const RAIL_ITEM =
  'relative mx-1 flex flex-col items-center justify-center gap-[5px] rounded-[6px] px-0.5 py-2.5 text-center text-[11px] leading-[1.15] text-muted-foreground hover:bg-accent hover:text-foreground data-[active]:bg-accent data-[active]:text-foreground data-[active]:after:absolute data-[active]:after:inset-y-2 data-[active]:after:left-0 data-[active]:after:w-0.5 data-[active]:after:bg-foreground [&_svg]:size-5'

function RailLink({
  to,
  label,
  icon: Icon,
  active,
}: {
  to: string
  label: string
  icon: LucideIcon
  active: boolean
}) {
  return (
    <Link
      to={to}
      className={RAIL_ITEM}
      data-active={active || undefined}
      aria-current={active ? 'page' : undefined}
    >
      <Icon aria-hidden />
      <span>{label}</span>
    </Link>
  )
}

/** Portfolio, screening and data sources: the rest of the Plotea app. */
export function PloteaPageLinks({ className }: { className?: string }) {
  const pathname = useRouterState({
    select: (state) => state.location.pathname,
  })
  return (
    <nav aria-label="Plotea" className={cn('flex flex-col gap-0.5', className)}>
      {PAGES.map((page) => (
        <RailLink
          key={page.to}
          {...page}
          active={pathname.startsWith(page.to)}
        />
      ))}
    </nav>
  )
}

/**
 * The left rail of Plotea's pages other than the map, where the analysis
 * panel's module rail plays this part: the way back to the analysis, then
 * the other pages.
 */
export function PloteaRail() {
  return (
    <aside
      className="flex w-[72px] shrink-0 flex-col gap-0.5 border-r border-border bg-background py-2"
      data-testid="plotea-rail"
    >
      <RailLink to="/site" label="Analizy" icon={LandPlot} active={false} />
      <PloteaPageLinks className="border-t border-border pt-0.5" />
    </aside>
  )
}
